import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { searchProducts, createProduct, catalogueMeta, getPriceList, saveListing } from "../../services/catalogueService";
import { errorToast, successToast } from "../Toasts/Toasts";

// CMS: the vendor-side products in Zoho CRM (Shopify-origin ones are
// never shown). Search, open, or create - and creating checks for a
// near match first, exactly as the vendor-pricing rules require.

const CatalogueProductList = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], nameSources: [], listingCategories: [] });
  // which products are already on the customer catalogue, and under what
  const [listed, setListed] = useState({});
  const [adding, setAdding] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", scientificName: "", category: "Plant" });
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await searchProducts(q)); } catch (e) { errorToast("Could not load products"); }
  }, [q]);
  const loadListed = useCallback(async () => {
    try {
      const rows = await getPriceList();
      setListed(Object.fromEntries(rows.map((r) => [r.zohoProductId, { status: r.status, category: r.category }])));
    } catch (e) { /* an empty catalogue is normal */ }
  }, []);
  useEffect(() => { loadListed(); }, [loadListed]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { catalogueMeta().then(setMeta).catch(() => {}); }, []);

  const create = async (force) => {
    setBusy(true);
    try {
      const r = await createProduct({ ...form, force });
      if (r?.success) { successToast(`Created ${r.data.name}`); setShow(false); setCandidates(null); setForm({ name: "", scientificName: "", category: "Plant" }); load(); }
      else if (r?.candidates) setCandidates(r.candidates);
      else errorToast(r?.message);
    } catch (e) {
      if (e?.candidates) setCandidates(e.candidates); else errorToast(e?.message || "Could not create");
    } finally { setBusy(false); }
  };

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="fw-bold mb-0">Products</h6>
            <div className="small text-muted">Zoho CRM vendor-side catalogue · {rows.length} shown</div>
          </div>
          <div className="d-flex gap-2">
            <Form.Control size="sm" placeholder="Search name or botanical name" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 260 }} autoFocus />
            <Button size="sm" variant="outline-success" onClick={() => setShow(true)}>New product</Button>
          </div>
        </div>
        <div className="card mt-2">
          <div className="card-body p-0">
            <table className="table table-hover table-sm mb-0">
              <thead><tr><th>Product</th><th>Botanical name</th><th>Category</th><th>Name source</th><th>Customers</th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td><Link to={`/catalogue/products/${p.id}`}>{p.name}</Link></td>
                    <td className="text-muted"><i>{p.scientificName || ""}</i></td>
                    <td>{p.category || ""}</td>
                    <td className="small text-muted">{p.nameSource || ""}</td>
                    <td>
                      {listed[p.id]
                        ? <Link to="/catalogue/price-list" className={`badge text-decoration-none bg-${listed[p.id].status === "Approved" ? "success" : listed[p.id].status === "Withdrawn" ? "secondary" : "warning text-dark"}`}>{listed[p.id].status}</Link>
                        : <Button size="sm" variant="outline-success" onClick={() => setAdding({ zohoProductId: p.id, displayName: p.name, category: "", description: "" })}>List for customers</Button>}
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={5} className="text-muted text-center py-4">Nothing matches.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal show={show} onHide={() => { setShow(false); setCandidates(null); }}>
        <Modal.Header closeButton><Modal.Title>New product in Zoho CRM</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Name (as the trade calls it)</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Botanical name (optional)</Form.Label><Form.Control value={form.scientificName} onChange={(e) => setForm({ ...form, scientificName: e.target.value })} /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Category</Form.Label>
            <Form.Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{meta.categories.map((c) => <option key={c}>{c}</option>)}</Form.Select>
          </Form.Group>
          {candidates && (
            <div className="alert alert-warning small mb-0">
              <div className="fw-semibold">These already exist — is it one of them?</div>
              {candidates.map((c) => <div key={c.id}><Link to={`/catalogue/products/${c.id}`}>{c.name}</Link>{c.scientificName ? <i className="text-muted"> ({c.scientificName})</i> : null}</div>)}
              <div className="mt-2">If it is genuinely a different plant, create it anyway.</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {candidates
            ? <Button variant="warning" disabled={busy} onClick={() => create(true)}>Create anyway — it's different</Button>
            : <Button variant="success" disabled={busy || !form.name.trim()} onClick={() => create(false)}>Create</Button>}
        </Modal.Footer>
      </Modal>
      <Modal show={!!adding} onHide={() => setAdding(null)}>
        <Modal.Header closeButton><Modal.Title>List for customers</Modal.Title></Modal.Header>
        {adding && <Modal.Body>
          <div className="small text-muted mb-3">
            It goes on as a draft. Societies see nothing until it has a price and you approve it.
          </div>
          <Form.Group className="mb-2">
            <Form.Label>Name customers see</Form.Label>
            <Form.Control value={adding.displayName} onChange={(e) => setAdding({ ...adding, displayName: e.target.value })} autoFocus />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Category</Form.Label>
            <Form.Select value={adding.category} onChange={(e) => setAdding({ ...adding, category: e.target.value })}>
              <option value="">— choose —</option>
              {(meta.listingCategories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control value={adding.description} onChange={(e) => setAdding({ ...adding, description: e.target.value })} placeholder="One line a society would find useful" />
          </Form.Group>
        </Modal.Body>}
        <Modal.Footer>
          <Button
            variant="success"
            disabled={!adding?.displayName?.trim() || !adding?.category}
            onClick={async () => {
              try {
                const r = await saveListing(adding.zohoProductId, adding);
                if (r?.success) { successToast(`${adding.displayName} added as a draft`); setAdding(null); loadListed(); }
                else errorToast(r?.message);
              } catch (e) { errorToast(e?.message); }
            }}
          >Add as draft</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default CatalogueProductList;
